import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Search } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import { errorMessage } from '../../../utils/apiResponse';
import { Dialog } from './AccountBits';
import type { CustomerXeroContact, XeroContactSummary } from '../../../types/nurseryAccounts';

/**
 * Which Xero contact a customer's invoices go to, on the customer's Account
 * tab. With no link, the first invoice matches a contact by account number or
 * exact name and makes a new one otherwise — so a customer already in Xero
 * under a slightly different name would be duplicated. An Accounts Admin links
 * it by hand here first.
 *
 * Renders nothing while Xero is not connected, and nothing for a site billed
 * to its head office: its invoices go to the head office's contact.
 */

const errorCls = 'bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words';

const contactDetail = (c: XeroContactSummary) =>
  [c.account_number ? `Account ${c.account_number}` : null, c.email, c.tax_number ? `ABN ${c.tax_number}` : null]
    .filter(Boolean).join(' · ');

interface PickerProps {
  customerId: number;
  customerName: string;
  current: CustomerXeroContact;
  onClose: () => void;
  onSaved: (next: CustomerXeroContact) => void;
}

function XeroContactPicker({ customerId, customerName, current, onClose, onSaved }: PickerProps) {
  const [term, setTerm] = useState(customerName);
  const [results, setResults] = useState<XeroContactSummary[] | null>(null);
  const [searched, setSearched] = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (text: string) => {
    const q = text.trim();
    if (q.length < 2) return;
    setSearching(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.searchXeroContacts(q);
      if (res.success) {
        setResults(res.data || []);
        setSearched(q);
      } else setError(errorMessage(res, 'Could not search Xero'));
    } catch (e) {
      setError(errorMessage(e, 'Could not search Xero'));
    } finally {
      setSearching(false);
    }
  }, []);

  // Open on what Xero has under the customer's own name.
  useEffect(() => { search(customerName); }, [search, customerName]);

  const save = async (contactId: string | null) => {
    setSaving(contactId ?? 'remove');
    setError(null);
    try {
      const res = await nurseryAccountsService.setCustomerXeroContact(customerId, contactId);
      if (res.success && res.data) onSaved(res.data);
      else setError(errorMessage(res, 'Could not save the Xero contact'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the Xero contact'));
    } finally {
      setSaving(null);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    search(term);
  };

  return (
    <Dialog
      title="Choose a Xero contact"
      subtitle={`For ${customerName}`}
      onClose={() => { if (!saving) onClose(); }}
      footer={<Button variant="outline" onClick={onClose} disabled={saving !== null}>Close</Button>}
    >
      <p className="text-sm text-neutral-700 break-words">
        New invoices and credit notes for this customer go to the contact you choose. Ones already sent stay where
        they are in Xero.
      </p>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-48">
          <label htmlFor="xero-contact-search" className="block text-sm font-medium text-neutral-700 mb-1">
            Search Xero by name, email or account number
          </label>
          <input id="xero-contact-search" type="search" value={term} onChange={(e) => setTerm(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <Button type="submit" variant="outline" disabled={searching || term.trim().length < 2}>
          <Search className="h-4 w-4 mr-1" /> {searching ? 'Searching…' : 'Search'}
        </Button>
      </form>

      {error && <div role="alert" className={errorCls}>{error}</div>}

      {results !== null && results.length === 0 && (
        <p className="text-sm text-neutral-600 break-words">
          Nothing in Xero matches “{searched}”. Try part of the name, or leave it unlinked and a new contact is made
          with the first invoice.
        </p>
      )}
      {results !== null && results.length > 0 && (
        <ul className="space-y-2">
          {results.map((c) => {
            const isCurrent = c.contact_id === current.contact_id;
            return (
              <li key={c.contact_id} className="border border-neutral-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1 basis-40">
                  <p className="text-sm font-medium text-neutral-900 break-words">{c.name}</p>
                  {contactDetail(c) && <p className="text-xs text-neutral-500 break-words">{contactDetail(c)}</p>}
                  {c.archived && <p className="text-xs text-amber-700">Archived in Xero</p>}
                </div>
                {isCurrent ? (
                  <span className="text-xs font-medium text-neutral-500">Linked now</span>
                ) : (
                  <Button size="sm" onClick={() => save(c.contact_id)} disabled={saving !== null || c.archived}>
                    {saving === c.contact_id ? 'Saving…' : 'Use this contact'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {current.contact_id && (
        <div className="border-t border-neutral-200 pt-3 space-y-2">
          <p className="text-sm text-neutral-700 break-words">
            Without a link, the next invoice looks in Xero for account number {current.account_number} or this
            customer’s exact name, and makes a new contact if there is neither.
          </p>
          <Button variant="outline" size="sm" onClick={() => save(null)} disabled={saving !== null}>
            {saving === 'remove' ? 'Removing…' : 'Remove the link'}
          </Button>
        </div>
      )}
    </Dialog>
  );
}

export default function XeroContactCard({ customerId, customerName }: { customerId: number; customerName: string }) {
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  const [data, setData] = useState<CustomerXeroContact | null>(null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryAccountsService.getCustomerXeroContact(customerId);
        if (live && res.success && res.data) setData(res.data);
      } catch {
        // The card is extra: the account still reads without it.
      }
    })();
    return () => { live = false; };
  }, [customerId]);

  if (!data || !data.xero_active || data.bills_to) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-2">
      <h2 className="text-base font-semibold text-neutral-800">Xero contact</h2>
      {data.contact ? (
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-900 break-words">{data.contact.name}</p>
          {contactDetail(data.contact) && <p className="text-xs text-neutral-500 break-words">{contactDetail(data.contact)}</p>}
          <p className="text-sm text-neutral-600 break-words mt-1">Invoices and credit notes go to this contact in Xero.</p>
          {data.contact.archived && (
            <p className="text-sm text-amber-800 break-words mt-1">
              This contact is archived in Xero. Restore it there, or choose another.
            </p>
          )}
        </div>
      ) : data.contact_id ? (
        <p className={errorCls}>{data.contact_error || 'The linked contact could not be read from Xero.'}</p>
      ) : (
        <p className="text-sm text-neutral-700 break-words">
          Not linked yet. The first invoice looks in Xero for a contact with exactly this name, and makes a new one
          if there is none. If this customer is already in Xero under a different name, choose it here first.
        </p>
      )}
      {isAdmin ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
            {data.contact_id ? 'Change the Xero contact' : 'Choose a Xero contact'}
          </Button>
        </div>
      ) : <p className="text-sm text-neutral-500 break-words">An Accounts Admin can change this.</p>}

      {picking && (
        <XeroContactPicker
          customerId={customerId}
          customerName={customerName}
          current={data}
          onClose={() => setPicking(false)}
          onSaved={(next) => { setData(next); setPicking(false); }}
        />
      )}
    </div>
  );
}
